import { 
  collection, doc, addDoc, updateDoc, deleteDoc, getDoc, setDoc,
  serverTimestamp, writeBatch, increment, getDocs, query, orderBy, limit, runTransaction
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, getSecondaryAuth } from '../config/firebase';
import { fullPermissions } from '../utils/permissions';
import { format } from 'date-fns';
import { toMoney, buildAuditFields, mergeUpdateAudit, getActiveProjectCostLines } from '../utils/helpers';
import { PROJECT_COST_TO_EXPENSE_CATEGORY } from '../utils/constants';

const stripActor = (data) => {
  const { auditActor, ...rest } = data;
  return { rest, auditActor };
};

const COMPANY_SETTINGS_DOC = 'company';

/** Next REF-style number from Settings (prefix + year + 5-digit seq). */
export const allocateReferenceNumber = async (kind) => {
  const year = String(new Date().getFullYear());
  const settingsRef = doc(db, 'settings', COMPANY_SETTINGS_DOC);
  const counterKey = kind === 'invoice' ? 'invoiceCounters' : 'quotationCounters';
  const prefixKey = kind === 'invoice' ? 'invoicePrefix' : 'quotationPrefix';
  const defaultPrefix = kind === 'invoice' ? 'REF' : 'QUO';

  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(settingsRef);
    const data = snap.exists() ? snap.data() : {};
    const prefix = String(data[prefixKey] || defaultPrefix).trim() || defaultPrefix;
    const counters = { ...(data[counterKey] || {}) };
    const next = (Number(counters[year]) || 0) + 1;
    counters[year] = next;

    transaction.set(
      settingsRef,
      {
        [prefixKey]: prefix,
        [counterKey]: counters,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );

    return `${prefix}-${year}-${String(next).padStart(5, '0')}`;
  });
};

export const ensureQuotationNumber = async (quotationId) => {
  const ref = doc(db, 'quotations', quotationId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return { success: false, error: 'Not found' };
  const data = snap.data();
  if (data.quotationNumber) {
    return { success: true, quotationNumber: data.quotationNumber };
  }
  const quotationNumber = await allocateReferenceNumber('quotation');
  await updateDoc(ref, { quotationNumber, updatedAt: serverTimestamp() });
  return { success: true, quotationNumber };
};

// QUOTATIONS
export const createQuotation = async (data) => {
  try {
    const quotationNumber = await allocateReferenceNumber('quotation');
    const quotationData = {
      ...data,
      quotationNumber,
      status: 'pending',
      projectAmount: 0,
      xamaaliAmount: 0,
      totalAmount: 0,
      adminSignature: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(collection(db, 'quotations'), quotationData);
    return { success: true, id: docRef.id, quotationNumber };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateQuotation = async (id, data) => {
  try {
    await updateDoc(doc(db, 'quotations', id), { ...data, updatedAt: serverTimestamp() });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteQuotation = async (id) => {
  try {
    await deleteDoc(doc(db, 'quotations', id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getQuotation = async (id) => {
  try {
    const docSnap = await getDoc(doc(db, 'quotations', id));
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    }
    return { success: false, error: 'Not found' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// INVOICES
export const generateInvoiceNumber = async () => allocateReferenceNumber('invoice');


export const createInvoice = async (quotationData, pricingData, adminSignature, auditActor = null) => {
  try {
    const invoiceNumber = await generateInvoiceNumber();
    
    const projectAmount = toMoney(pricingData.projectAmount);
    const xamaaliAmount = toMoney(pricingData.xamaaliAmount);
    const totalAmount = projectAmount + xamaaliAmount;

    const invoiceData = {
      invoiceNumber,
      quotationId: quotationData.id,
      
      // Customer info
      customerName: quotationData.customerName,
      phone: quotationData.phone,
      fromLocation: quotationData.fromLocation,
      toLocation: quotationData.toLocation,
      movingDate: quotationData.movingDate,
      
      // Service info
      serviceType: quotationData.serviceType,
      items: quotationData.items || [],
      
      // ✅ House Type Info
      houseType: quotationData.houseType || null,
      floorLevel: quotationData.floorLevel || null,
      biyaanoFeatures: quotationData.biyaanoFeatures || [],
      destinationType: quotationData.destinationType || null,
      destinationFloor: quotationData.destinationFloor || null,
      dabaqNameFrom: quotationData.dabaqNameFrom || '',
      dabaqNameTo: quotationData.dabaqNameTo || '',

      workShifts: quotationData.workShifts || [],
      shiftNotes: quotationData.shiftNotes || '',
      
      // Pricing — approved quotation money is revenue (dakhli)
      projectAmount,
      xamaaliAmount,
      totalAmount,
      
      // Payment
      paymentType: quotationData.paymentType,
      paymentAccountId: quotationData.paymentAccountId,
      accountNumber: pricingData.accountNumber || '',
      accountName: pricingData.accountName || '',
      
      // Signatures
      customerSignature: quotationData.customerSignature,
      adminSignature: adminSignature,
      
      // Revision history
      revisionHistory: [],
      
      // Metadata
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...buildAuditFields(auditActor)
    };
    
    const batch = writeBatch(db);
    
    // Create invoice
    const invoiceRef = doc(collection(db, 'invoices'));
    batch.set(invoiceRef, invoiceData);

    if (auditActor?.uid) {
      batch.set(
        doc(db, 'users', auditActor.uid),
        { invoiceCount: increment(1) },
        { merge: true }
      );
    }
    
    // Update quotation status
    const quotationRef = doc(db, 'quotations', quotationData.id);
    batch.update(quotationRef, {
      status: 'invoiced',
      invoiceId: invoiceRef.id,
      projectAmount,
      xamaaliAmount,
      totalAmount,
      adminSignature: adminSignature,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...mergeUpdateAudit(auditActor)
    });
    
    // Create transaction
    const transactionRef = doc(collection(db, 'transactions'));
    batch.set(transactionRef, {
      invoiceId: invoiceRef.id,
      invoiceNumber,
      type: 'revenue',
      paymentAccountId: quotationData.paymentAccountId || pricingData.paymentAccountId || null,
      paymentType: quotationData.paymentType,
      amount: totalAmount,
      customerName: quotationData.customerName,
      customerPhone: quotationData.phone,
      month: format(new Date(), 'yyyy-MM'),
      transactionDate: serverTimestamp(),
      createdAt: serverTimestamp(),
      ...buildAuditFields(auditActor || pricingData.auditActor)
    });
    
    await batch.commit();
    return { success: true, id: invoiceRef.id, invoiceNumber };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateInvoice = async (id, data, userId, userName, reason = '') => {
  try {
    const docRef = doc(db, 'invoices', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return { success: false, error: 'Not found' };
    
    const currentData = docSnap.data();
    const changes = {};
    Object.keys(data).forEach(key => {
      if (currentData[key] !== data[key]) {
        changes[key] = { old: currentData[key], new: data[key] };
      }
    });
    
    const revisionEntry = {
      editedAt: new Date().toISOString(),
      editedBy: userId,
      editedByName: userName,
      changes,
      reason
    };
    
    await updateDoc(docRef, {
      ...data,
      totalAmount: toMoney(data.projectAmount ?? currentData.projectAmount) + toMoney(data.xamaaliAmount ?? currentData.xamaaliAmount),
      revisionHistory: [...(currentData.revisionHistory || []), revisionEntry],
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteInvoice = async (id) => {
  try {
    await deleteDoc(doc(db, 'invoices', id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getInvoice = async (id) => {
  try {
    const docSnap = await getDoc(doc(db, 'invoices', id));
    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
    }
    return { success: false, error: 'Not found' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// EMPLOYEES
export const createEmployee = async (data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    const employeeData = {
      ...rest,
      jobsHandled: 0,
      revenueInvolved: 0,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...buildAuditFields(auditActor)
    };
    const docRef = await addDoc(collection(db, 'employees'), employeeData);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateEmployee = async (id, data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    await updateDoc(doc(db, 'employees', id), {
      ...rest,
      updatedAt: serverTimestamp(),
      ...mergeUpdateAudit(auditActor)
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteEmployee = async (id) => {
  try {
    await updateDoc(doc(db, 'employees', id), { isActive: false, updatedAt: serverTimestamp() });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// PAYMENT ACCOUNTS
export const createPaymentAccount = async (data) => {
  try {
    const accountData = {
      ...data,
      totalReceived: 0,
      transactionCount: 0,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(collection(db, 'paymentAccounts'), accountData);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updatePaymentAccount = async (id, data) => {
  try {
    await updateDoc(doc(db, 'paymentAccounts', id), { ...data, updatedAt: serverTimestamp() });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deletePaymentAccount = async (id) => {
  try {
    await updateDoc(doc(db, 'paymentAccounts', id), { isActive: false, updatedAt: serverTimestamp() });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createManualTransaction = async (accountId, amount, type, accountName, paymentType, auditActor = null) => {
  try {
    const finalAmount = type === 'add' ? Number(amount) : -Number(amount);
    
    await addDoc(collection(db, 'transactions'), {
      invoiceId: 'manual',
      invoiceNumber: type === 'add' ? 'Manual Add' : 'Manual Less',
      paymentAccountId: accountId,
      paymentType: paymentType,
      amount: finalAmount,
      customerName: 'Admin Manual Edit',
      customerPhone: '',
      month: format(new Date(), 'yyyy-MM'),
      transactionDate: serverTimestamp(),
      createdAt: serverTimestamp(),
      ...buildAuditFields(auditActor)
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============ EXPENSES ============

export const createExpense = async (data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    const expenseData = {
      ...rest,
      employeeId: rest.employeeId || null,
      employeeName: rest.employeeName || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...buildAuditFields(auditActor)
    };

    // If linked to an employee, use batch to atomically update both
    if (data.employeeId) {
      const batch = writeBatch(db);
      const expenseRef = doc(collection(db, 'expenses'));
      batch.set(expenseRef, expenseData);

      // Increment employee jobsHandled (+1) and revenueInvolved (+amount)
      const employeeRef = doc(db, 'employees', data.employeeId);
      batch.update(employeeRef, {
        jobsHandled: increment(1),
        revenueInvolved: increment(rest.amount || 0),
        updatedAt: serverTimestamp()
      });

      await batch.commit();
      return { success: true, id: expenseRef.id };
    } else {
      const docRef = await addDoc(collection(db, 'expenses'), expenseData);
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateExpense = async (id, data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    await updateDoc(doc(db, 'expenses', id), {
      ...rest,
      updatedAt: serverTimestamp(),
      ...mergeUpdateAudit(auditActor)
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteExpense = async (id) => {
  try {
    // Read the expense first to check if it's linked to an employee
    const expenseSnap = await getDoc(doc(db, 'expenses', id));
    if (!expenseSnap.exists()) return { success: false, error: 'Not found' };

    const expenseData = expenseSnap.data();

    if (expenseData.employeeId) {
      // Use batch to atomically delete expense AND decrement employee stats
      const batch = writeBatch(db);
      batch.delete(doc(db, 'expenses', id));

      const employeeRef = doc(db, 'employees', expenseData.employeeId);
      batch.update(employeeRef, {
        jobsHandled: increment(-1),
        revenueInvolved: increment(-(expenseData.amount || 0)),
        updatedAt: serverTimestamp()
      });

      await batch.commit();
    } else {
      await deleteDoc(doc(db, 'expenses', id));
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============ INVENTORY ============

export const createInventoryItem = async (data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    const itemData = {
      ...rest,
      price: Number(rest.price) || 0,
      quantity: Number(rest.quantity) || 1,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...buildAuditFields(auditActor)
    };
    const docRef = await addDoc(collection(db, 'inventory'), itemData);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateInventoryItem = async (id, data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    await updateDoc(doc(db, 'inventory', id), {
      ...rest,
      price: Number(rest.price) || 0,
      quantity: Number(rest.quantity) || 1,
      updatedAt: serverTimestamp(),
      ...mergeUpdateAudit(auditActor)
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteInventoryItem = async (id) => {
  try {
    await deleteDoc(doc(db, 'inventory', id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============ PROJECT COST SHEETS ============
// Firestore collection: `projectCostSheets`
// lines[]: { lineNo, businessKey, businessName, amount, supervisor, date, included }

export const listProjectCostSheets = async () => {
  try {
    const q = query(collection(db, 'projectCostSheets'), orderBy('updatedAt', 'desc'), limit(200));
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

export const getProjectCostSheet = async (id) => {
  try {
    const docSnap = await getDoc(doc(db, 'projectCostSheets', id));
    if (!docSnap.exists()) return { success: false, error: 'Not found' };
    return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createProjectCostSheet = async (data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    const payload = {
      ...rest,
      postedToKharashAt: null,
      postedExpenseIds: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...buildAuditFields(auditActor)
    };
    const docRef = await addDoc(collection(db, 'projectCostSheets'), payload);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateProjectCostSheet = async (id, data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    await updateDoc(doc(db, 'projectCostSheets', id), {
      ...rest,
      updatedAt: serverTimestamp(),
      ...mergeUpdateAudit(auditActor)
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteProjectCostSheet = async (id) => {
  try {
    await deleteDoc(doc(db, 'projectCostSheets', id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============ DALALE (dealers) ============
// Collection: `dalale` — name, phone, degmo, commissionPercent

export const getDalale = async (id) => {
  try {
    const docSnap = await getDoc(doc(db, 'dalale', id));
    if (!docSnap.exists()) return { success: false, error: 'Not found' };
    return { success: true, data: { id: docSnap.id, ...docSnap.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createDalale = async (data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    const payload = {
      ...rest,
      commissionPercent: toMoney(rest.commissionPercent),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...buildAuditFields(auditActor)
    };
    const docRef = await addDoc(collection(db, 'dalale'), payload);
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateDalale = async (id, data) => {
  try {
    const { rest, auditActor } = stripActor(data);
    await updateDoc(doc(db, 'dalale', id), {
      ...rest,
      commissionPercent: toMoney(rest.commissionPercent),
      updatedAt: serverTimestamp(),
      ...mergeUpdateAudit(auditActor)
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteDalale = async (id) => {
  try {
    await deleteDoc(doc(db, 'dalale', id));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============ APARTMENT REGISTRY (spreadsheet) ============
// Document: apartmentRegistry/main — rows[]

const APARTMENT_REGISTRY_DOC_ID = 'main';

export const getApartmentRegistry = async () => {
  try {
    const docSnap = await getDoc(doc(db, 'apartmentRegistry', APARTMENT_REGISTRY_DOC_ID));
    if (!docSnap.exists()) return { success: true, data: { rows: [] } };
    const data = docSnap.data();
    return {
      success: true,
      data: { id: docSnap.id, rows: Array.isArray(data.rows) ? data.rows : [], ...data }
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const saveApartmentRegistry = async (rows, auditActor = null) => {
  try {
    const ref = doc(db, 'apartmentRegistry', APARTMENT_REGISTRY_DOC_ID);
    await setDoc(
      ref,
      {
        rows: Array.isArray(rows) ? rows : [],
        updatedAt: serverTimestamp(),
        ...mergeUpdateAudit(auditActor)
      },
      { merge: true }
    );
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const postProjectCostSheetToKharash = async (sheetId, auditActor = null) => {
  try {
    const sheetResult = await getProjectCostSheet(sheetId);
    if (!sheetResult.success) return sheetResult;
    const sheet = sheetResult.data;
    if (sheet.postedToKharashAt) {
      return { success: false, error: 'Horey ayaa loo post gareeyay' };
    }

    const expenseIds = [];
    const lines = getActiveProjectCostLines(sheet.lines || []);

    for (const line of lines) {
      const amount = toMoney(line.amount);
      if (amount <= 0) continue;
      const category = PROJECT_COST_TO_EXPENSE_CATEGORY[line.businessKey]
        || PROJECT_COST_TO_EXPENSE_CATEGORY[line.businessName]
        || 'adeegyada';
      const result = await createExpense({
        category,
        description: line.businessName || line.businessKey,
        amount,
        date: line.date || sheet.jobDate || new Date(),
        invoiceId: sheet.invoiceId || null,
        projectCostSheetId: sheetId,
        auditActor
      });
      if (result.success && result.id) expenseIds.push(result.id);
    }

    await updateDoc(doc(db, 'projectCostSheets', sheetId), {
      postedToKharashAt: serverTimestamp(),
      postedExpenseIds: expenseIds,
      updatedAt: serverTimestamp(),
      ...mergeUpdateAudit(auditActor)
    });

    return { success: true, expenseIds };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const revertProjectCostSheetFromKharash = async (sheetId, auditActor = null) => {
  try {
    const sheetResult = await getProjectCostSheet(sheetId);
    if (!sheetResult.success) return sheetResult;
    const sheet = sheetResult.data;
    if (!sheet.postedToKharashAt) {
      return { success: false, error: 'Lama post gareyn' };
    }

    const expenseIds = Array.isArray(sheet.postedExpenseIds) ? sheet.postedExpenseIds : [];
    for (const expenseId of expenseIds) {
      const del = await deleteExpense(expenseId);
      if (!del.success) {
        return { success: false, error: del.error || 'Kharash lama tirtiri karo' };
      }
    }

    await updateDoc(doc(db, 'projectCostSheets', sheetId), {
      postedToKharashAt: null,
      postedExpenseIds: [],
      updatedAt: serverTimestamp(),
      ...mergeUpdateAudit(auditActor)
    });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// APP USERS (Firebase Auth + Firestore profile)
export const createAppUser = async ({
  email,
  password,
  displayName,
  role = 'staff',
  permissions = {},
  auditActor = null
}) => {
  try {
    const secondaryAuth = getSecondaryAuth();
    const cred = await createUserWithEmailAndPassword(secondaryAuth, email.trim(), password);
    const uid = cred.user.uid;
    const profile = {
      email: email.trim().toLowerCase(),
      displayName: displayName?.trim() || email.split('@')[0],
      role,
      permissions: role === 'admin' ? fullPermissions() : permissions,
      isActive: true,
      invoiceCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...buildAuditFields(auditActor)
    };
    await setDoc(doc(db, 'users', uid), profile);
    return { success: true, uid };
  } catch (error) {
    let message = error.message;
    if (error.code === 'auth/email-already-in-use') message = 'Email-kan hore ayaa loo isticmaalay';
    if (error.code === 'auth/weak-password') message = 'Password-ku waa inuu ugu yaraan 6 xaraf yahay';
    return { success: false, error: message };
  }
};

export const updateAppUserProfile = async (uid, data, auditActor = null) => {
  try {
    const { displayName, role, permissions, isActive } = data;
    const payload = {
      updatedAt: serverTimestamp(),
      ...mergeUpdateAudit(auditActor)
    };
    if (displayName !== undefined) payload.displayName = displayName.trim();
    if (role !== undefined) {
      payload.role = role;
      payload.permissions = role === 'admin' ? fullPermissions() : (permissions || {});
    } else if (permissions !== undefined) {
      payload.permissions = permissions;
    }
    if (isActive !== undefined) payload.isActive = isActive;
    await updateDoc(doc(db, 'users', uid), payload);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};